import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../app/store';
import styled from 'styled-components';
import { selectPage } from '../features/pageSlice';
import ItemsPage from './items_page';
import BoxesPage from './boxes_page';
import AddUpdateBox from './manage_box_page';
import AddUpdateItem from './manage_item_page';
import { FaBox, FaCopy, FaSignOutAlt, FaFlag, FaWallet, FaTicketAlt, FaChartLine, FaExchangeAlt, FaUsers, FaProjectDiagram, FaBoxOpen } from 'react-icons/fa'; // Importing icons
import { IoSettings } from "react-icons/io5";
import BannersPage from './banners_page';
import AddUpdateBanner from './manage_banner_page';
import SettingsPage from './settings_page';
import VideoCustomizationFormForOneItem from './manage_box_video_one_item';
import { getAvatarImagePath, getImagePath } from '../utils/utils';
import LevelBoxesPage from './level_boxes_page';
import VideoCustomizationForm from './manage_box_video';
import AllVideosGeneratorForBox from './manage_box_all_videos';
import BoxVideosPage from './BoxVideosPage';
// import NftsPage from './nfts_page';
import CouponsPage from './coupons_page';
import { AclBootstrap, fetchUserInformation, getUserAvatar, logoutUser, MerkleTreesComponent, PermissionGate, ProtectedRoute, useModuleDispatch, UserProfileComponent, UsersPage, WalletsPage, WithdrawalsPage } from 'keycloakauthmodule';
import RewardBoxesPage from './reward_boxes_page';
import GiftCategoriesPage from './GiftCategoriesPage';

// Styled components
const DashboardContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const SideMenu = styled.div<{ isVisible: boolean }>`
  width: 320px;
  background-color: #f8f8f8;
  padding: 20px;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  flex-shrink: 0; /* Prevent shrinking */
  
  @media (max-width: 1024px) {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 1;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none; /* Remove underline */
  margin-bottom: 10px;
  color: #333; /* Dark text color */
  
  img {
    width: 60px; /* Increase logo size */
    height: 60px;
    border-radius: 50%;
    margin-right: 15px; /* Increase spacing */
  }

  h1 {
    font-size: 24px; /* Larger font size */
    font-weight: 700;
    margin: 0;
    text-transform: uppercase; /* Uppercase text */
    letter-spacing: 1px; /* Letter spacing for elegance */
  }
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  margin-bottom: 10px;
`;

const ProfilePicture = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 10px;
`;

const ProfileName = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
  text-align: center;
`;

const ProfileEmail = styled.div`
  font-size: 14px;
  color: #555;
  text-align: center;
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin: 15px 0;
    display: flex;
    align-items: center;

    a, button {
      text-decoration: none;
      color: #333;
      font-size: 16px;
      display: flex;
      align-items: center;
      transition: color 0.3s;
      margin-left: 10px;
    }

    a:hover, button:hover {
      color: #007bff;
    }

    button {
      background: none;
      border: none;
      cursor: pointer;
    }
  }
`;

const Icon = styled.div`
  font-size: 20px;
  color: #007bff;
`;

const ContentArea = styled.div<{ isSideMenuVisible: boolean }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin-left: 0;
  padding: 15px; /* Adjusted padding to 15px on all sides */
  border-radius: 10px; /* Adding rounded corners */
  overflow: auto; /* Enable scrolling if content exceeds height */
  transition: margin-left 0.3s;

  @media (max-width: 1024px) {
    margin-left: 0;
    width: calc(100% - 30px); /* Adjusted for padding */
  }

  @media (min-width: 1024px) {
    width: calc(100% - 320px - 30px); /* Adjusted for side menu and padding */
  }
`;

const Heading = styled.div`
  height: 100px; /* Increased height */
  display: flex;
  align-items: center;
  justify-content: flex-start; /* Align items to the left */
  border-bottom: 2px solid #e0e0e0; /* Subtle border */
  background-color: #ffffff; /* Clean background */
  padding: 0 20px; /* Padding on left and right */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Light shadow for depth */
  position: relative;
  width: 100%; /* Ensure full width */

  /* Align items to the left */
  & > * {
    margin: 0;
  }

  h2 {
    font-size: 24px; /* Adjust font size for better appearance */
    font-weight: 300; /* Light font weight */
    color: #555555; /* Softer color */
    letter-spacing: 0.5px; /* Slight letter spacing */
    text-align: left; /* Align text to the left */
    margin-left: 40px; /* Space between button and text */
  }

  @media (max-width: 1024px) {
    h2 {
      font-size: 20px; /* Adjust font size for smaller screens */
    }
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #007bff; /* Highlight color for button */
  cursor: pointer;
  transition: color 0.3s;
  
  &:hover {
    color: #0056b3; /* Darker color on hover */
  }

  @media (max-width: 1024px) {
    display: block;
  }
`;


const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MainContent = styled.div`
  flex-grow: 1;
  padding: 20px;
  background-color: #f0f0f0;
`;

const DashboardPage: React.FC = () => {
  const [isSideMenuVisible, setSideMenuVisible] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>(); // ✅ Properly typed dispatch
  const dispatchForModule = useModuleDispatch();
  const selectedPage = useSelector((state: RootState) => state.page.selectedPage);
  const user = useSelector((state: RootState) => state.authSlice.user);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Extract user picture URL
  let pic = getAvatarImagePath();
  if (user?.userImage && user.userImage.trim().length > 1) {
    pic = getUserAvatar(user?.userImage);
  }

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  const toggleSideMenu = () => {
    setSideMenuVisible(!isSideMenuVisible);
  };

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setDob(user.dateOfBirth || '');
      setGender(user.gender || '');
      setPhone(user.phone || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setSideMenuVisible(true);
      } else {
        setSideMenuVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/dashboard/wallets")) {
      dispatch(selectPage("wallets"));
    } else if (location.pathname.startsWith("/dashboard/users")) {
      dispatch(selectPage("users"));
    } else if (location.pathname.startsWith("/dashboard/withdrawals")) {
      dispatch(selectPage("withdrawals"));
    } else if (location.pathname.startsWith("/dashboard/merkle_tree")) {
      dispatch(selectPage("merkle_tree"));
    } else if (location.pathname.startsWith("/dashboard/items")) {
      dispatch(selectPage("items"));
    } else if (location.pathname.startsWith("/dashboard/boxes")) {
      dispatch(selectPage("boxes"));
    } else if (location.pathname.startsWith("/dashboard/banners")) {
      dispatch(selectPage("banners"));
    } else if (location.pathname.startsWith("/dashboard/settings")) {
      dispatch(selectPage("settings"));
      // } else if (location.pathname.startsWith("/dashboard/nfts")) {
      //   dispatch(selectPage("nfts"));
    } else if (location.pathname.startsWith("/dashboard/coupons")) {
      dispatch(selectPage("coupons"));
    } else if (location.pathname.startsWith("/dashboard/reward-boxes")) {
      dispatch(selectPage("reward-boxes"));
    } else if (location.pathname.startsWith("/dashboard/gift-categories")) {
      dispatch(selectPage("gift-categories"));
    } else if (location.pathname.startsWith("/dashboard/level-boxes")) {
      dispatch(selectPage("level-boxes"));
    } else {
      dispatch(selectPage("items"));
    }
  }, [location.pathname, dispatch]);

  // useEffect(() => {
  //   dispatchForModule(fetchUserInformation());
  // }, [dispatchForModule]);

  const handleMenuItemClick = (page: string) => {
    if (window.innerWidth <= 1024) {
      toggleSideMenu();
    }
    dispatch(selectPage(page));
  };

  const getDashboardTitle = () => {
    switch (selectedPage) {
      case 'wallets':
        return 'Wallets';
      case 'users':
        return 'Users';
      case 'withdrawals':
        return 'Withdrawals';
      case 'merkle_tree':
        return 'Merkle Tree';
      case 'items':
        return 'Items';
      case 'boxes':
        return 'Boxes';
      case 'banners':
        return 'Banners';
      case 'settings':
        return 'Settings';
      // case 'nfts':
      //   return 'NFTS';
      case 'coupons':
        return 'Coupons';
      case 'level-boxes':
        return 'Level Boxes';
      case 'reward-boxes':
        return "Reward Boxes";
      case 'gift-categories':
        return "Gift Categories";
      default:
        return 'Items';
    }
  };

  return (
    <AclBootstrap blockUntilLoaded fallback={<div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#f8f9fa"
    }}>
      <div style={{
        width: "48px",
        height: "48px",
        border: "5px solid rgba(0,0,0,0.1)",
        borderTopColor: "#007bff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }} />
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>}>
      <DashboardContainer>
        <SideMenu isVisible={isSideMenuVisible}>
          <CloseButton onClick={toggleSideMenu}>×</CloseButton>

          {/* Logo Section */}
          <LogoLink to="/">
            <img src="/logo192.png" alt="BoxChaos Logo" />
            <h1>BoxChaos</h1>
          </LogoLink>

          {/* Profile Section */}
          <ProfileSection>
            <ProfilePicture src={pic} alt="User Profile" />
            <ProfileName>{firstName} {lastName}</ProfileName>
            <ProfileEmail>{email}</ProfileEmail>
          </ProfileSection>

          {/* Navigation Links */}
          <MenuList>
            <PermissionGate requireAclId="wallets_list">
              <li>
                <Icon><FaWallet /></Icon>
                <Link to="/dashboard/wallets" onClick={() => handleMenuItemClick('wallets')}>
                  Wallets
                </Link>
              </li>
            </PermissionGate>

            <PermissionGate requireAclId="crypto_withdraw_history">
              <li>
                <Icon><FaExchangeAlt /></Icon>
                <Link to="/dashboard/withdrawals" onClick={() => handleMenuItemClick('withdrawals')}>
                  Withdrawals
                </Link>
              </li>
            </PermissionGate>
            <PermissionGate requireAclId="users_get">
              <li>
                <Icon><FaUsers /></Icon>
                <Link to="/dashboard/users" onClick={() => handleMenuItemClick('users')}>
                  Users
                </Link>
              </li>
            </PermissionGate>
            <PermissionGate requireAclId="sol_merkle_trees">
              <li>
                <Icon><FaProjectDiagram /></Icon>
                <Link to="/dashboard/merkle_tree" onClick={() => handleMenuItemClick('merkle_tree')}>
                  Merkle Tree
                </Link>
              </li>
            </PermissionGate>


            <li>
              <Icon><FaCopy /></Icon>
              <Link to="/dashboard/items" onClick={() => handleMenuItemClick('items')}>Items</Link>
            </li>
            <li>
              <Icon><FaBox /></Icon>
              <Link to="/dashboard/boxes" onClick={() => handleMenuItemClick('boxes')}>Boxes</Link>
            </li>
            <li>
              <Icon><FaFlag /></Icon>
              <Link to="/dashboard/banners" onClick={() => handleMenuItemClick('banners')}>Banners</Link>
            </li>
            <li>
              <Icon><FaChartLine /></Icon>
              <Link to="/dashboard/level-boxes" onClick={() => handleMenuItemClick('level-boxes')}>Level Boxes</Link>
            </li>
            <li>
              <Icon><FaBoxOpen /></Icon>
              <Link to="/dashboard/reward-boxes" onClick={() => handleMenuItemClick('reward-boxes')}>Reward Boxes</Link>
            </li>
            <li>
              <Icon><FaBoxOpen /></Icon>
              <Link to="/dashboard/gift-categories" onClick={() => handleMenuItemClick('gift-categories')}>Gift Categories</Link>
            </li>
            <li>
              <Icon><IoSettings /></Icon>
              <Link to="/dashboard/settings" onClick={() => handleMenuItemClick('settings')}>Settings</Link>
            </li>
            {/* <li>
            <Icon><FaFileImage /></Icon>
            <Link to="nfts" onClick={() => handleMenuItemClick('nfts')}>NFTS</Link>
          </li> */}
            <li>
              <Icon><FaTicketAlt /></Icon>
              <Link to="/dashboard/coupons" onClick={() => handleMenuItemClick('coupons')}>Coupons</Link>
            </li>
            <li>
              <Icon><FaSignOutAlt /></Icon>
              <button onClick={handleLogout}>Logout</button>
            </li>
          </MenuList>
        </SideMenu>

        <ContentArea isSideMenuVisible={isSideMenuVisible}>
          <Heading>
            <MenuButton onClick={toggleSideMenu}>☰</MenuButton>
            <h2>{getDashboardTitle()}</h2>
          </Heading>

          <MainContent>
            {selectedPage === "wallets" && (
              <Routes>
                <Route
                  path="/wallets/"
                  element={
                                          <WalletsPage />

                    // <ProtectedRoute requireAclId="wallets_list" redirectTo='/'>
                    //   <WalletsPage />
                    // </ProtectedRoute>
                  }
                />
              </Routes>
            )}
            {selectedPage === "users" && (
              <Routes>
                <Route path="/users/" element={<UsersPage />} />
                <Route path="/users/details" element={<UserProfileComponent />} />
                <Route path="/users/details/:userId" element={<UserProfileComponent />} />
              </Routes>
            )}

            {selectedPage === "withdrawals" && (
              <Routes>
                <Route
                  path="/withdrawals/"
                  element={
                    <ProtectedRoute requireAclId="crypto_withdraw_history" redirectTo='/'>
                      <WithdrawalsPage />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            )}

            {selectedPage === "merkle_tree" && (
              <Routes>
                <Route
                  path="/merkle_tree/"
                  element={
                    <ProtectedRoute requireAclId="sol_merkle_trees" redirectTo='/'>
                      <MerkleTreesComponent />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            )}
            {(selectedPage === "items" || selectedPage === "home") && (
              <Routes>
                <Route path="/items/" element={<ItemsPage />} />
                <Route path="/items/details" element={<AddUpdateItem />} />
                <Route path="/items/details/:itemId" element={<AddUpdateItem />} />
              </Routes>
            )}
            {selectedPage === "boxes" && (
              <Routes>
                <Route path="/boxes/" element={<BoxesPage />} />
                <Route path="/boxes/details" element={<AddUpdateBox />} />
                <Route path="/boxes/details/:boxId" element={<AddUpdateBox />} />
                <Route path="/boxes/box-video/" element={<VideoCustomizationForm />} />
                <Route path="/boxes/box-winning-video/" element={<VideoCustomizationFormForOneItem />} />
                <Route path="/boxes/box-all-videos/:boxId" element={<AllVideosGeneratorForBox />} />
                <Route path="/boxes/videos/:boxId" element={<BoxVideosPage />} />
              </Routes>
            )}
            {selectedPage === "banners" && (
              <Routes>
                <Route path="/banners/" element={<BannersPage />} />
                <Route path="/banners/details" element={<AddUpdateBanner />} />
                <Route path="/banners/details/:bannerId" element={<AddUpdateBanner />} />
              </Routes>
            )}
            {selectedPage === "settings" && (
              <Routes>
                <Route path="/settings/" element={<SettingsPage />} />
              </Routes>
            )}
            {/* {selectedPage === "nfts" && (
            <Routes>
              <Route path="/nfts/" element={<NftsPage />} />
            </Routes>
          )} */}
            {selectedPage === "coupons" && (
              <Routes>
                <Route path="/coupons/" element={<CouponsPage />} />
              </Routes>
            )}

            {selectedPage === "reward-boxes" && (
              <Routes>
                <Route path="/reward-boxes/" element={<RewardBoxesPage />} />
              </Routes>
            )}

            {selectedPage === "gift-categories" && (
              <Routes>
                <Route path="/gift-categories/" element={<GiftCategoriesPage />} />
              </Routes>
            )}

            {selectedPage === "level-boxes" && (
              <Routes>
                <Route path="/level-boxes/" element={<LevelBoxesPage />} />
              </Routes>
            )}
          </MainContent>
        </ContentArea>
      </DashboardContainer>
    </AclBootstrap>
  );
};

export default DashboardPage;
